// src/components/Login/LoginAffiliateForm.tsx
import React, { useEffect, useState } from "react";
import {
  CardContent,
  CardActions,
  CardHeader,
  InputLabel,
  Grid,
  Box,
  Button,
  Link,
  Typography
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "formik-material-ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { SessionSorageInstance } from "../../Services/SessionStorageService";
import { setuserRol } from "../../Redux/GeneralSlice";
import AffiliateService from "../../Services/http/AffiliateService";
import IndexService from "../../Services/http/IndexService";
import userRoles from "../../configs/userRolesConfigs";
import LockIcon from '@mui/icons-material/Lock';

import VerificationCodeForm from "./VerificationCodeForm";
import HubspotUpdateSuccessDialog from "../Dialogs/HubspotUpdateSuccessDialog";

import "./loginForm.scss";
import { useTranslation } from 'react-i18next';
import OnboardingLayout from "../Layout/OnboardingLayout";

const initialValues = {
  email: "",
  password: "",
  recaptcha: "",
};

const validationSchema = Yup.object().shape({
  email: Yup.string().required("Required"),
  password: Yup.string().required("Required!"),
});

const LoginAffiliateForm = () => {
  const { t, i18n } = useTranslation("loginForm");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const navigate = useNavigate();
  const [isShowErrorMessage, setIsShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userToken, setUserToken] = useState("");
  const [responseLogin, setResponseLogin] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSuccessFirstStep, setIsSuccessFirstStep] = useState(false);
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("hubspot_val")) {
      handleHubspotVal(params.get("hubspot_val"));
      removeParam("hubspot_val");
    }
  }, []);

  function removeParam(key: string) {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete(key);
    setSearchParams(newParams.toString());
  }

  const handleHubspotVal = (hubspot_val: string) => {
    IndexService.updateContactStatusInHubSpot(hubspot_val).then(response => {
      if (response?.message === "success") setIsOpenSuccessDialog(true);
      else alert(t(`errors.${response?.message}`) || t("errors.generic"));
    });
  };

  const getErrorMessage = (responseMessage: string) => {
    switch (responseMessage) {
      case null:
        return t("errors.connection");
      case "success":
        return "success";
      case "Password expired":
        return t("errors.passwordExpired");
      case "Unauthenticated: invalid email or password":
        return t("errors.invalidCredentials");
      case "unauthorized":
        return t("errors.notAffiliate");
      default:
        return t("errors.generic");
    }
  };

  const onSubmitLogin = async (values: any) => {
    setIsShowErrorMessage(false);
    const response = await AffiliateService.loginAffiliate(values);

    if (response && response["token"]) {
      const userRole = response["user"]["role"];
      if (userRole !== userRoles.affiliate) {
        setErrorMessage("User is not an affiliate.");
        setIsShowErrorMessage(true);
        return;
      }

      setResponseLogin(response);
      setUserToken(response["token"]);
      onLoginSuccess(response);
    } else {
      let message: string =
        getErrorMessage(
          JSON.parse(JSON.stringify(response))?.message || null
        ) || "";
      setErrorMessage(message);
      setIsShowErrorMessage(true);
    }
  };

  const onLoginSuccess = (response: any) => {
    SessionSorageInstance.setUserDetails(response);
    dispatch(setuserRol(SessionSorageInstance.getUserRole()));
    navigate("/affiliate-layout");
  };

  return (
    <OnboardingLayout rightClassName="center-items">
      <Grid className="login-grid" container justifyContent="center" sx={{ margin: 'auto' }}>
        <Grid item md={6}>
          <CardHeader title={t("title")} className="text-center pb-0" />
          <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 500, color: '#333' }}>
            {t("affiliatePortal")}
          </Typography>

          {!isSuccessFirstStep && <p className="text-center">{t("subtitle")}</p>}
          {!isSuccessFirstStep && (
            <Formik
              initialValues={initialValues}
              onSubmit={onSubmitLogin}
              validationSchema={validationSchema}
            >
              {({ dirty, isValid, values }) => (
                <Form>
                  <CardContent>
                    <Grid item container spacing={1} justifyContent="center">
                      <Grid item xs={12} sm={6} md={12}>
                        <InputLabel>{t("email")}</InputLabel>
                        <Field
                          label=""
                          fullWidth
                          required
                          placeholder={t("placeholders.email")}
                          name="email"
                          value={values.email}
                          component={TextField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={12}>
                        <InputLabel>{t("password")}</InputLabel>
                        <Field
                          label=""
                          variant="outlined"
                          fullWidth
                          placeholder={t("placeholders.password")}
                          name="password"
                          value={values.password}
                          type="password"
                          required
                          component={TextField}
                        />
                      </Grid>
                      {isShowErrorMessage && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <div className="error-message">{errorMessage}</div>
                        </div>
                      )}
                    </Grid>
                  </CardContent>
                  <CardActions>
                    <Button
                      disabled={!dirty || !isValid}
                      type="submit"
                      className="button-login"
                      startIcon={<LockIcon className="white-color" />}
                    >
                      {t("submit")}
                    </Button>
                  </CardActions>
                  <p className="text-center gray-two-color">
                    {t("no_account")}
                    <Link className="blue-color pl-3p" underline="hover" href="/register-affiliate">
                      {t("signup")}
                    </Link>
                  </p>
                </Form>
              )}
            </Formik>
          )}
          {isSuccessFirstStep && (
            <VerificationCodeForm token={userToken} onLoginSuccess={() => onLoginSuccess(responseLogin)} />
          )}
        </Grid>
      </Grid>
      {isOpenSuccessDialog && <HubspotUpdateSuccessDialog />}
    </OnboardingLayout>
  );
};

export default LoginAffiliateForm;
